'use client';

import Icon from '@/components/ui/AppIcon';

export default function CancellationPolicySection() {
  const cancellationTiers = [
    {
      period: '30+ Days Before Check-in',
      refund: '100%',
      description: 'Full refund minus processing fee',
      icon: 'CheckBadgeIcon',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      period: '15-29 Days Before Check-in',
      refund: '75%',
      description: 'Partial refund of total booking amount',
      icon: 'ShieldCheckIcon',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      period: '7-14 Days Before Check-in',
      refund: '50%',
      description: 'Half refund of total booking amount',
      icon: 'ExclamationTriangleIcon',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      period: 'Less Than 7 Days',
      refund: '0%',
      description: 'No refund - full amount charged',
      icon: 'XCircleIcon',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
  ];

  const specialProvisions = [
    {
      title: 'Corporate Accounts',
      description: 'Flexible cancellation terms with 48-hour notice for established corporate partners',
      icon: 'BuildingOfficeIcon',
    },
    {
      title: 'Extended Stays (30+ Days)',
      description: 'Custom cancellation policies negotiated at time of booking',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Force Majeure',
      description: 'Full refund for cancellations due to natural disasters, medical emergencies, or travel restrictions',
      icon: 'ShieldExclamationIcon',
    },
  ];

  return (
    <section id="cancellation-policy" className="py-16 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="ArrowPathIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Cancellation Policy</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Flexible Cancellation Terms
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Tiered refund schedule based on cancellation timing, with special provisions for corporate accounts and extended stays.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {cancellationTiers.map((tier, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-6 border-2 border-border hover:border-primary transition-all duration-300 hover:shadow-elevated"
            >
              <div className={`w-14 h-14 ${tier.bgColor} rounded-lg flex items-center justify-center mb-4`}>
                <Icon name={tier.icon as any} size={28} className={tier.color} />
              </div>
              <div className="mb-3">
                <div className={`text-3xl font-bold ${tier.color} mb-1`}>{tier.refund}</div>
                <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                  Refund
                </div>
              </div>
              <h3 className="font-semibold text-text-primary mb-2">{tier.period}</h3>
              <p className="text-sm text-text-secondary">{tier.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-muted rounded-2xl p-8 lg:p-12">
          <h3 className="font-display text-2xl font-bold text-primary mb-6 text-center">
            Special Provisions
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {specialProvisions.map((provision, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-subtle">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={provision.icon as any} size={24} className="text-accent" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">{provision.title}</h4>
                <p className="text-sm text-text-secondary leading-relaxed">{provision.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Important Notes</h4>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>• Cancellation requests must be submitted in writing via email or booking portal</li>
                <li>• Processing fee of ₹500 applies to all cancellations with refunds</li>
                <li>• Refunds processed within 7-10 business days to original payment method</li>
                <li>• No-shows are treated as same-day cancellations (0% refund)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}